import {
  createContext,
  FC,
  SyntheticEvent,
  useCallback,
  useContext,
  useMemo,
  useState,
} from 'react';

type AccordionContextType = {
  isOpen: (id: string) => boolean;
  setOpen: (id: string, open: boolean) => void;
};

const AccordionContext = createContext<AccordionContextType>({
  isOpen: () => true,
  setOpen: () => {},
});

type Props = {
  id: string;
};

type AccordionState = {
  [key: string]: boolean;
};

type UseAccordionPanel = [
  boolean,
  (_: SyntheticEvent, isExpanded: boolean) => void
];

export const useAccordion = () => useContext(AccordionContext);

export const useAccordionPanel = (id: string): UseAccordionPanel => {
  const { isOpen, setOpen } = useAccordion();

  const handleChange = useCallback(
    (_: SyntheticEvent, isExpanded: boolean) => {
      setOpen(id, isExpanded);
    },
    [id, setOpen]
  );

  return [isOpen(id), handleChange];
};

const AccordionController: FC<Props> = ({ id, children }) => {
  const initial: AccordionState = useMemo(() => {
    const storedState = localStorage.getItem(`accordion_${id}`);
    return storedState ? JSON.parse(storedState) : {};
  }, [id]);

  const [state, setState] = useState<AccordionState>(initial);

  const isOpen = useCallback((id: string) => state[id] ?? true, [state]);

  const setOpen = useCallback(
    (accordionId: string, open: boolean) => {
      const newState = {
        ...state,
        [accordionId]: open,
      };

      setState(newState);
      localStorage.setItem(`accordion_${id}`, JSON.stringify(newState));
    },
    [id, state]
  );

  return (
    <AccordionContext.Provider value={{ isOpen, setOpen }}>
      {children}
    </AccordionContext.Provider>
  );
};

export default AccordionController;
